import React from 'react';
import { Box, Button, Tabs, Tab } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Template } from '../../model/Template';
import { TemplateTag } from '../../model/TemplateTag';
import { Language } from '../../model/Language';
import { createTemplate, editTemplate } from '../../api/templateClient';
import TemplateConfiguration from './TemplateConfiguration';
import LocalizedTemplateConfiguration from './LocalizedTemplateConfiguration';

interface Props {
  method: 'POST' | 'PUT';
  template?: Template;
  templateTags: TemplateTag[];
}

const TemplateEditor: React.FC<Props> = ({ method, template, templateTags }) => {
    console.log("HELLO")
    console.log(templateTags)
    console.log(template)
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const { t } = useTranslation();
  const [activeLanguage, setActiveLanguage] = React.useState<Language | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  
  // Initialize available languages from existing template or start empty
  const [availableLanguages, setAvailableLanguages] = React.useState<Language[]>(() => {
    if (template?.localizations && Object.keys(template.localizations).length > 0) {
      const languages = Object.keys(template.localizations) as Language[];
      return languages;
    }
    return [];
  });
  
  // Set initial active language
  React.useEffect(() => {
    if (availableLanguages.length > 0 && !activeLanguage) {
      setActiveLanguage(availableLanguages[0]);
    }
  }, [availableLanguages, activeLanguage]);
  
  const methods = useForm<Template>({
    defaultValues: {
      templateId: template?.templateId || undefined,
      difficulty: template?.difficulty || '',
      templateTags: template?.templateTags || [],
      localizations: template?.localizations || {}
    }
  });

  const handleSubmit = async (data: Template) => {
    setIsSubmitting(true);
    try {
      // Validate that at least one localization exists (matches backend validation)
      const localizations = data.localizations || {};
      if (Object.keys(localizations).length === 0) {
        throw new Error('Template must have at least one localization');
      }
      
      let response;
      if (method === 'PUT') {
        // Update existing template
        response = await editTemplate(data, template!.templateId!, customerId!);
      } else {
        // Create new template
        response = await createTemplate(data, customerId!);
      }
      
      if (!response.ok) {
        throw new Error(`Failed to ${method === 'PUT' ? 'update' : 'create'} template`);
      }
      
      const savedTemplate = await response.json();
      
      if (method === 'PUT') {
        // Navigate back to the template view after update
        navigate(`/customers/${customerId!}/templates/${template!.templateId}`);
      } else {
        // Navigate to the new template view after creation
        navigate(`/customers/${customerId!}/templates/${savedTemplate.templateId}`);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLanguageChange = (event: React.SyntheticEvent, newValue: Language) => {
    setActiveLanguage(newValue);
  };

  const addLanguage = (language: Language) => {
    if (!availableLanguages.includes(language)) {
      setAvailableLanguages(prev => [...prev, language]);
      setActiveLanguage(language);
      // Initialize empty localization for the new language
      methods.setValue(`localizations.${language}` as any, {
        language,
        name: '',
        senderAddress: '',
        replyToAddress: '',
        subject: '',
        body: '',
        learningContents: []
      });
    }
  };

  const removeLanguage = (language: Language) => {
    setAvailableLanguages(prev => prev.filter(lang => lang !== language));
    // Remove the localization data
    const currentValues = methods.getValues();
    const { [language]: removed, ...remainingLocalizations } = currentValues.localizations as any;
    methods.setValue('localizations', remainingLocalizations);
    
    // Switch to another language if we removed the active one
    if (activeLanguage === language) {
      const nextLanguage = availableLanguages.find(lang => lang !== language);
      setActiveLanguage(nextLanguage || null);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleSubmit as any)}>
        {/* Language-agnostic configuration */}
        <TemplateConfiguration templateTags={templateTags} />
        
        {/* Language-specific configuration */}
        <Box sx={{ mt: 2 }}>
          {availableLanguages.length === 0 ? (
            /* No languages added yet - show add buttons */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', py: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <h3>{t('templateEditor.addTemplateContent')}</h3>
                <p>{t('templateEditor.chooseLanguageToStart')}</p>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={() => addLanguage(Language.DE)}
                  sx={{ minWidth: 120 }}
                >
                  {t('templateEditor.addGerman')}
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => addLanguage(Language.EN)}
                  sx={{ minWidth: 120 }}
                >
                  {t('templateEditor.addEnglish')}
                </Button>
              </Box>
            </Box>
          ) : (
            /* Languages exist - show tabs and management */
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Tabs value={activeLanguage} onChange={handleLanguageChange}>
                  {availableLanguages.map(language => (
                    <Tab key={language} label={language.toUpperCase()} value={language} />
                  ))}
                </Tabs>
                
                {/* Language management buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!availableLanguages.includes(Language.DE) && (
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => addLanguage(Language.DE)}
                    >
                      {t('templateEditor.addGermanShort')}
                    </Button>
                  )}
                  {!availableLanguages.includes(Language.EN) && (
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => addLanguage(Language.EN)}
                    >
                      {t('templateEditor.addEnglishShort')}
                    </Button>
                  )}
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="error"
                    onClick={() => activeLanguage && removeLanguage(activeLanguage)}
                  >
                    {t('templateEditor.removeLanguage', { language: activeLanguage?.toUpperCase() })}
                  </Button>
                </Box>
              </Box>
              
              {availableLanguages.map(language => (
                <Box key={language} sx={{ display: language === activeLanguage ? 'block' : 'none' }}>
                  <LocalizedTemplateConfiguration language={language} />
                </Box>
              ))}
            </>
          )}
        </Box>
        
        {/* Submit button */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isSubmitting || availableLanguages.length === 0}
          >
            {isSubmitting ? t('templateEditor.saving') : (method === 'PUT' ? t('templateEditor.updateTemplate') : t('templateEditor.createTemplate'))}
          </Button>
          <Button type="button" variant="outlined" onClick={() => navigate('..')} disabled={isSubmitting}>
            {t('templateEditor.cancel')}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default TemplateEditor;
